/**
 * Priya — CreatorKhata's AI assistant.
 *
 * Floating chat bubble (bottom-right) + slide-in chat panel.
 * Works for both anonymous (landing) and authenticated users.
 *
 * State persistence:
 *   - Open/closed state and conversation history live in localStorage
 *     under 'ck-priya-state', so navigation between pages doesn't reset chat
 *   - Cleared when user clicks "Clear chat" or after 7 days of inactivity
 *
 * UX rules:
 *   - First open shows welcome message + 4 suggested-question chips
 *   - Esc closes panel, Enter sends message (Shift+Enter = newline)
 *   - Loading dots animation while Priya thinks
 *   - Rate-limit (429) shows friendly "try again in a minute" message
 *   - Mobile: panel takes full screen instead of side panel
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatAPI } from '../services/api';

// ── Constants ──────────────────────────────────────────────────────────────

const STORAGE_KEY      = 'ck-priya-state';
const STORAGE_VERSION  = 1;
const MAX_HISTORY      = 30;          // client-side soft cap; backend enforces 20
const STATE_TTL_MS     = 7 * 24 * 60 * 60 * 1000;   // 7 days

const SUGGESTED_QUESTIONS = [
  { icon: '🚀', text: 'What is CreatorKhata?' },
  { icon: '💰', text: 'How does pricing work?' },
  { icon: '📊', text: 'How do I track brand deals?' },
  { icon: '🧾', text: 'How does GST invoicing work?' },
];

const WELCOME_MESSAGE = {
  role: 'assistant',
  content:
    "Hi! I'm Priya, your CreatorKhata assistant. Ask me anything about the app, " +
    "brand deals, GST invoices, TDS, or growing your creator business in India.",
};

// ── Storage helpers ────────────────────────────────────────────────────────

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.v !== STORAGE_VERSION) return null;
    if (Date.now() - (parsed.savedAt || 0) > STATE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      v:        STORAGE_VERSION,
      savedAt:  Date.now(),
      ...state,
    }));
  } catch (_) {}
}

// ── Component ──────────────────────────────────────────────────────────────

export default function PriyaChatbot() {
  const { user }  = useAuth();
  const location  = useLocation();

  const initial = loadState() || {};
  const [open,        setOpen]        = useState(initial.open || false);
  const [messages,    setMessages]    = useState(initial.messages || [WELCOME_MESSAGE]);
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);

  const scrollRef     = useRef(null);
  const inputRef      = useRef(null);

  // Persist whenever state changes
  useEffect(() => {
    saveState({ open, messages: messages.slice(-MAX_HISTORY) });
  }, [open, messages]);

  // Auto-scroll to bottom when new message arrives or panel opens
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Esc closes panel
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Send a message
  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    const next    = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setLoading(true);

    try {
      // Strip the welcome message before sending — backend doesn't need it
      const historyForApi = next.filter(m => m !== WELCOME_MESSAGE
                                          && !(m.role === 'assistant' && m.content === WELCOME_MESSAGE.content));
      const context = {
        plan:        user?.plan || null,
        currentPage: location?.pathname || '/',
      };
      const res = await chatAPI.ask(historyForApi, context);
      const reply = res?.data?.message || "Hmm, I didn't get a reply. Try again?";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      let errMsg = "Sorry, I couldn't reply just now. Please try again in a moment.";
      if (err?.response?.status === 429) {
        const code = err.response.data?.error;
        if (code === 'AI_DAILY_LIMIT') {
          errMsg = "You've hit today's chat quota. Resets at midnight IST. "
                 + (user?.plan === 'PRO'
                     ? "Even on PRO this is capped to manage costs."
                     : "Upgrade to PRO for higher daily quota.");
        } else {
          errMsg = "Lots of activity right now — please wait a minute and try again.";
        }
      } else if (err?.response?.status >= 500 || !err?.response) {
        errMsg = "Connection hiccup. Check your internet and try again?";
      }
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg, isError: true }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, user, location]);

  const clearChat = () => {
    setMessages([WELCOME_MESSAGE]);
  };

  // ── Styles (inline so component is self-contained) ───────────────────────

  const bubbleStyle = {
    position: 'fixed', bottom: '1.5rem', right: '1.5rem',
    height: 48, padding: '0 1.1rem 0 0.85rem', borderRadius: '24px',
    background: 'linear-gradient(135deg, #4dba7a, #1a7a4a)',
    border: 'none', cursor: 'pointer', zIndex: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    boxShadow: '0 6px 24px rgba(26,122,74,0.45)',
    transition: 'transform 0.15s, box-shadow 0.15s',
    fontSize: '0.9rem', fontWeight: 700, color: 'white',
    fontFamily: 'inherit', letterSpacing: '0.01em',
  };

  const panelOverlay = {
    position: 'fixed', inset: 0, zIndex: 999,
    background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
    padding: '1.5rem',
    animation: 'fadeIn 0.18s ease-out',
  };

  const panelStyle = {
    background: 'white', borderRadius: '20px',
    width: '100%', maxWidth: '420px', height: '85vh', maxHeight: '720px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    animation: 'slideInRight 0.25s cubic-bezier(0.4,0,0.2,1)',
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #0f1117, #1a2535)',
    padding: '1rem 1.25rem', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    color: 'white',
  };

  const messagesAreaStyle = {
    flex: 1, padding: '1rem', overflowY: 'auto',
    background: '#f8f9fb',
    display: 'flex', flexDirection: 'column', gap: '0.75rem',
  };

  const inputAreaStyle = {
    padding: '0.75rem', borderTop: '1px solid #e5e7eb', background: 'white',
    display: 'flex', gap: '0.5rem', alignItems: 'flex-end',
  };

  const userBubbleStyle = {
    alignSelf: 'flex-end', maxWidth: '85%',
    background: '#1a7a4a', color: 'white',
    padding: '0.6rem 0.9rem', borderRadius: '16px 16px 4px 16px',
    fontSize: '0.88rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  };

  const assistantBubbleStyle = (isError) => ({
    alignSelf: 'flex-start', maxWidth: '90%',
    background: isError ? '#fef2f2' : 'white',
    color: isError ? '#991b1b' : '#0f1117',
    padding: '0.65rem 0.9rem', borderRadius: '16px 16px 16px 4px',
    fontSize: '0.88rem', lineHeight: 1.55,
    border: isError ? '1px solid #fecaca' : '1px solid #e5e7eb',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  });

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="Chat with Priya — CreatorKhata's AI assistant"
          style={bubbleStyle}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          aria-label="Open Priya chatbot"
        >
          <span style={{ fontSize: '1.15rem' }}>💬</span>
          <span>Ask Priya</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={panelOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Priya chat panel"
        >
          <div onClick={e => e.stopPropagation()} style={panelStyle}>

            {/* Header */}
            <div style={headerStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4dba7a, #1a7a4a)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.1rem',
                }}>
                  💬
                </div>
                <div>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '0.95rem' }}>
                    Priya
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)' }}>
                    CreatorKhata assistant
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  onClick={clearChat}
                  title="Clear chat"
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px',
                    color: 'rgba(255,255,255,0.7)', width: 32, height: 32, cursor: 'pointer',
                    fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-label="Clear chat history"
                >
                  🗑
                </button>
                <button
                  onClick={() => setOpen(false)}
                  title="Close"
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px',
                    color: 'rgba(255,255,255,0.7)', width: 32, height: 32, cursor: 'pointer',
                    fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  aria-label="Close chat"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Messages area */}
            <div ref={scrollRef} style={messagesAreaStyle}>
              {messages.map((m, i) => (
                m.role === 'user' ? (
                  <div key={i} style={userBubbleStyle}>{m.content}</div>
                ) : (
                  <div key={i} style={assistantBubbleStyle(m.isError)}>{m.content}</div>
                )
              ))}

              {/* Suggested questions — only shown when at welcome state */}
              {messages.length === 1 && messages[0] === WELCOME_MESSAGE && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q.text)}
                      style={{
                        textAlign: 'left',
                        padding: '0.55rem 0.8rem',
                        background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px',
                        cursor: 'pointer', fontSize: '0.82rem', color: '#374151',
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#1a7a4a'; e.currentTarget.style.background = '#eef7f2'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = 'white'; }}
                    >
                      <span style={{ fontSize: '1rem' }}>{q.icon}</span>
                      <span>{q.text}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Loading dots */}
              {loading && (
                <div style={{ ...assistantBubbleStyle(false), display: 'inline-flex', gap: '4px', padding: '0.75rem 0.9rem' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1a7a4a', animation: 'priyaPulse 1.2s infinite both' }} />
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1a7a4a', animation: 'priyaPulse 1.2s 0.2s infinite both' }} />
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1a7a4a', animation: 'priyaPulse 1.2s 0.4s infinite both' }} />
                </div>
              )}
            </div>

            {/* Input area */}
            <div style={inputAreaStyle}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Ask Priya anything…"
                rows={1}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '0.55rem 0.75rem',
                  border: '1px solid #e5e7eb', borderRadius: '10px',
                  fontSize: '0.88rem', lineHeight: 1.5,
                  resize: 'none', maxHeight: '120px',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
                aria-label="Type your message to Priya"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                style={{
                  background: input.trim() && !loading
                    ? 'linear-gradient(135deg, #4dba7a, #1a7a4a)'
                    : '#e5e7eb',
                  color: 'white', border: 'none', borderRadius: '10px',
                  width: 38, height: 38, cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  fontSize: '1.05rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-label="Send message"
              >
                ➤
              </button>
            </div>

            {/* Footer disclaimer */}
            <div style={{
              padding: '0.4rem 0.75rem', background: '#f8f9fb',
              borderTop: '1px solid #e5e7eb',
              fontSize: '0.65rem', color: '#9ca3af', textAlign: 'center',
            }}>
              Priya is AI. For specific tax/legal advice, consult a CA or lawyer.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
